import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api'

interface AIEnrichmentPanelProps {
  profileData: any
  query: string
}

interface BreachAnnotation {
  source: string
  severity: string
  why_it_matters: string
  exposed_risk: string
  data_quality: string
}

interface EntityConnection {
  entity_a: string
  entity_b: string
  relationship: string
  confidence: string
  evidence: string
}

interface PivotSuggestion {
  action: string
  target: string
  tool: string
  expected_value: string
  priority: string
}

interface RiskIndicator {
  indicator: string
  severity: string
  category: string
}

interface PasswordAnalysis {
  total_exposed: number
  plaintext_count: number
  reuse_likelihood: string
  reuse_reasoning: string
  crackability: string
  immediate_risk: string
}

interface Enrichments {
  breach_annotations?: BreachAnnotation[]
  password_analysis?: PasswordAnalysis
  entity_connections?: EntityConnection[]
  pivot_suggestions?: PivotSuggestion[]
  risk_indicators?: RiskIndicator[]
}

const SEVERITY_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  CRITICAL: { bg: 'bg-red-600/20', border: 'border-red-500/50', text: 'text-red-400' },
  HIGH: { bg: 'bg-orange-600/20', border: 'border-orange-500/50', text: 'text-orange-400' },
  MEDIUM: { bg: 'bg-yellow-600/20', border: 'border-yellow-500/50', text: 'text-yellow-400' },
  LOW: { bg: 'bg-green-600/20', border: 'border-green-500/50', text: 'text-green-400' },
}

const CATEGORY_LABELS: Record<string, string> = {
  credential_exposure: 'Credential Exposure',
  identity_theft: 'Identity Theft Risk',
  corporate_risk: 'Corporate Risk',
  govt_doc_leak: 'Government Document Leak',
  malware_infection: 'Malware Infection',
  financial_risk: 'Financial Risk',
}

const TOOL_COLORS: Record<string, string> = {
  RocketReach: 'text-purple-400 border-purple-500/40',
  OCCRP: 'text-amber-400 border-amber-500/40',
  Sanctions: 'text-red-400 border-red-500/40',
  LeakIX: 'text-cyan-400 border-cyan-500/40',
  Shodan: 'text-orange-400 border-orange-500/40',
  VirusTotal: 'text-blue-400 border-blue-500/40',
  Sycek: 'text-emerald-400 border-emerald-500/40',
}

export function AIEnrichmentPanel({ profileData, query }: AIEnrichmentPanelProps) {
  const [enrichments, setEnrichments] = useState<Enrichments | null>(null)
  const [entitiesDiscovered, setEntitiesDiscovered] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    risks: true,
    breaches: false,
    passwords: false,
    connections: false,
    pivots: false,
  })

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  useEffect(() => {
    if (profileData && profileData.breachSources?.length > 0) {
      fetchEnrichments()
    }
  }, [profileData?.breachSources?.length])

  const fetchEnrichments = async () => {
    if (!profileData) return
    setLoading(true)
    setError(null)
    try {
      const result: any = await apiClient.getBreachAIEnrichments({
        query,
        breachSources: profileData.breachSources,
        personalInfo: profileData.personalInfo,
        contactDetails: profileData.contactDetails,
        stealerLogs: profileData.stealerLogs || [],
        webResults: profileData.webResults || [],
      })
      setEnrichments(result.enrichments || {})
      setEntitiesDiscovered(result.entities_discovered || null)
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch AI enrichments')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="border border-cyan-500/30 rounded-lg bg-gradient-to-r from-cyan-900/10 via-black to-cyan-900/10 p-5"
      >
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full"
          />
          <div>
            <div className="text-cyan-400 font-mono text-sm font-semibold">AI ENRICHMENT IN PROGRESS</div>
            <div className="text-gray-500 text-xs mt-0.5">Analyzing {profileData?.breachSources?.length || 0} sources, extracting entities, rating risks...</div>
          </div>
        </div>
      </motion.div>
    )
  }

  if (error && !enrichments) {
    return null // Silently fail - AI enrichment is supplementary
  }

  if (!enrichments || (!enrichments.risk_indicators?.length && !enrichments.breach_annotations?.length && !enrichments.pivot_suggestions?.length)) {
    return null
  }

  const riskIndicators = enrichments.risk_indicators || []
  const breachAnnotations = enrichments.breach_annotations || []
  const passwordAnalysis = enrichments.password_analysis
  const connections = enrichments.entity_connections || []
  const pivots = enrichments.pivot_suggestions || []

  const criticalCount = riskIndicators.filter(r => r.severity === 'CRITICAL').length
  const highCount = riskIndicators.filter(r => r.severity === 'HIGH').length

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="border border-cyan-500/30 rounded-lg bg-gradient-to-b from-gray-900/80 to-black overflow-hidden"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-cyan-500/20 bg-cyan-900/10">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse shrink-0" />
            <span className="text-cyan-400 font-mono text-xs font-bold tracking-widest truncate">AI ENRICHMENT ENGINE</span>
          </div>
          <button
            onClick={fetchEnrichments}
            className="text-[10px] font-mono text-cyan-400 hover:text-cyan-300 border border-cyan-500/30 px-2 py-1 rounded transition-colors shrink-0"
          >
            Re-enrich
          </button>
        </div>
        {(criticalCount > 0 || highCount > 0) && (
          <div className="flex items-center gap-1.5 mt-2">
            {criticalCount > 0 && (
              <Badge className="bg-red-600 text-white text-[10px] font-mono border-0 px-2">{criticalCount} CRITICAL</Badge>
            )}
            {highCount > 0 && (
              <Badge className="bg-orange-600 text-white text-[10px] font-mono border-0 px-2">{highCount} HIGH</Badge>
            )}
          </div>
        )}
      </div>

      <div className="p-5 space-y-5">

        {/* Risk Indicators */}
        {riskIndicators.length > 0 && (
          <Section
            title="RISK INDICATORS"
            count={riskIndicators.length}
            expanded={expandedSections.risks}
            onToggle={() => toggleSection('risks')}
            color="red"
          >
            <div className="grid grid-cols-1 gap-2">
              {riskIndicators.map((risk, i) => {
                const style = SEVERITY_STYLES[risk.severity] || SEVERITY_STYLES.MEDIUM
                return (
                  <div key={i} className={`${style.bg} border ${style.border} rounded-lg p-3`}>
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={`${style.text} bg-transparent border ${style.border} text-xs font-mono`}>
                        {risk.severity}
                      </Badge>
                      <span className="text-gray-500 text-xs font-mono">
                        {CATEGORY_LABELS[risk.category] || risk.category}
                      </span>
                    </div>
                    <div className="text-gray-200 text-sm">{risk.indicator}</div>
                  </div>
                )
              })}
            </div>
          </Section>
        )}

        {/* Password Analysis */}
        {passwordAnalysis && passwordAnalysis.total_exposed > 0 && (
          <Section
            title="PASSWORD INTELLIGENCE"
            count={passwordAnalysis.total_exposed}
            countLabel="exposed"
            expanded={expandedSections.passwords}
            onToggle={() => toggleSection('passwords')}
            color="orange"
          >
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-black/40 border border-orange-500/30 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-orange-400 font-mono">{passwordAnalysis.total_exposed}</div>
                <div className="text-xs text-gray-500 font-mono">Total Exposed</div>
              </div>
              <div className={`bg-black/40 border ${passwordAnalysis.plaintext_count > 0 ? 'border-red-500/50' : 'border-green-500/30'} rounded-lg p-3 text-center`}>
                <div className={`text-2xl font-bold font-mono ${passwordAnalysis.plaintext_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {passwordAnalysis.plaintext_count}
                </div>
                <div className="text-xs text-gray-500 font-mono">Plaintext</div>
              </div>
              <div className="bg-black/40 border border-yellow-500/30 rounded-lg p-3 text-center">
                <div className={`text-lg font-bold font-mono ${passwordAnalysis.reuse_likelihood === 'HIGH' ? 'text-red-400' : passwordAnalysis.reuse_likelihood === 'MEDIUM' ? 'text-yellow-400' : 'text-green-400'}`}>
                  {passwordAnalysis.reuse_likelihood}
                </div>
                <div className="text-xs text-gray-500 font-mono">Reuse Risk</div>
              </div>
            </div>
            <div className="space-y-2">
              {passwordAnalysis.reuse_reasoning && (
                <div className="text-sm text-gray-300">
                  <span className="text-orange-400 font-mono text-xs mr-2">REUSE:</span>
                  {passwordAnalysis.reuse_reasoning}
                </div>
              )}
              {passwordAnalysis.crackability && (
                <div className="text-sm text-gray-300">
                  <span className="text-orange-400 font-mono text-xs mr-2">CRACK:</span>
                  {passwordAnalysis.crackability}
                </div>
              )}
              {passwordAnalysis.immediate_risk && (
                <div className="text-sm text-gray-300">
                  <span className="text-red-400 font-mono text-xs mr-2">RISK:</span>
                  {passwordAnalysis.immediate_risk}
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Entity Connections */}
        {connections.length > 0 && (
          <Section
            title="IDENTITY CORRELATION"
            count={connections.length}
            countLabel="links"
            expanded={expandedSections.connections}
            onToggle={() => toggleSection('connections')}
            color="purple"
          >
            <div className="space-y-2">
              {connections.map((conn, i) => (
                <div key={i} className="bg-black/40 border border-purple-500/20 rounded-lg p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-purple-300 text-xs bg-purple-900/30 px-2 py-0.5 rounded">{conn.entity_a}</code>
                      <span className="text-gray-500 text-xs">
                        {conn.relationship === 'same_person' ? '= same as' :
                         conn.relationship === 'family' ? '~ family of' :
                         conn.relationship === 'employer' ? '@ works at' :
                         conn.relationship === 'alias' ? '~ alias of' :
                         conn.relationship === 'shared_device' ? '~ shared device' :
                         `~ ${conn.relationship}`}
                      </span>
                      <code className="text-purple-300 text-xs bg-purple-900/30 px-2 py-0.5 rounded">{conn.entity_b}</code>
                    </div>
                    <div className="text-gray-500 text-xs mt-1">{conn.evidence}</div>
                  </div>
                  <Badge className={`text-xs font-mono border-0 ${
                    conn.confidence === 'HIGH' ? 'bg-green-600/30 text-green-400' :
                    conn.confidence === 'MEDIUM' ? 'bg-yellow-600/30 text-yellow-400' :
                    'bg-gray-600/30 text-gray-400'
                  }`}>
                    {conn.confidence}
                  </Badge>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Breach Annotations */}
        {breachAnnotations.length > 0 && (
          <Section
            title="BREACH SOURCE ANALYSIS"
            count={breachAnnotations.length}
            countLabel="sources"
            expanded={expandedSections.breaches}
            onToggle={() => toggleSection('breaches')}
            color="cyan"
          >
            <div className="space-y-2">
              {breachAnnotations.map((ann, i) => {
                const style = SEVERITY_STYLES[ann.severity] || SEVERITY_STYLES.MEDIUM
                return (
                  <div key={i} className={`bg-black/40 border ${style.border} rounded-lg p-3`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm">{ann.source}</span>
                        <Badge className={`${style.text} bg-transparent border ${style.border} text-xs font-mono`}>
                          {ann.severity}
                        </Badge>
                      </div>
                      <span className={`text-xs font-mono ${ann.data_quality === 'HIGH' ? 'text-green-400' : ann.data_quality === 'MEDIUM' ? 'text-yellow-400' : 'text-gray-500'}`}>
                        Quality: {ann.data_quality}
                      </span>
                    </div>
                    <div className="text-gray-300 text-sm">{ann.why_it_matters}</div>
                    <div className="text-gray-500 text-xs mt-1">{ann.exposed_risk}</div>
                  </div>
                )
              })}
            </div>
          </Section>
        )}

        {/* Pivot Suggestions */}
        {pivots.length > 0 && (
          <Section
            title="INVESTIGATION PIVOTS"
            count={pivots.length}
            countLabel="leads"
            expanded={expandedSections.pivots}
            onToggle={() => toggleSection('pivots')}
            color="emerald"
          >
            <div className="space-y-2">
              {pivots.map((pivot, i) => {
                const toolColor = TOOL_COLORS[pivot.tool] || 'text-gray-400 border-gray-500/40'
                return (
                  <div key={i} className="bg-black/40 border border-emerald-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Badge className={`${toolColor} bg-transparent border text-xs font-mono`}>
                          {pivot.tool}
                        </Badge>
                        <Badge className={`text-xs font-mono border-0 ${
                          pivot.priority === 'HIGH' ? 'bg-red-600/30 text-red-400' :
                          pivot.priority === 'MEDIUM' ? 'bg-yellow-600/30 text-yellow-400' :
                          'bg-gray-600/30 text-gray-400'
                        }`}>
                          {pivot.priority}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-gray-200 text-sm">{pivot.action}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-cyan-300 text-xs bg-cyan-900/20 px-2 py-0.5 rounded">{pivot.target}</code>
                      <span className="text-gray-500 text-xs">{pivot.expected_value}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </Section>
        )}

        {/* Discovered Entities Summary */}
        {entitiesDiscovered && (
          <div className="border-t border-gray-800/50 pt-3">
            <div className="text-gray-500 text-xs font-mono mb-2">EXTRACTED ENTITIES</div>
            <div className="flex flex-wrap gap-1.5">
              {entitiesDiscovered.emails?.slice(0, 5).map((e: string, i: number) => (
                <code key={`e-${i}`} className="text-xs bg-blue-900/20 text-blue-400 px-2 py-0.5 rounded border border-blue-500/20">{e}</code>
              ))}
              {entitiesDiscovered.phones?.slice(0, 5).map((p: string, i: number) => (
                <code key={`p-${i}`} className="text-xs bg-green-900/20 text-green-400 px-2 py-0.5 rounded border border-green-500/20">{p}</code>
              ))}
              {entitiesDiscovered.usernames?.slice(0, 5).map((u: string, i: number) => (
                <code key={`u-${i}`} className="text-xs bg-purple-900/20 text-purple-400 px-2 py-0.5 rounded border border-purple-500/20">{u}</code>
              ))}
              {entitiesDiscovered.ips?.slice(0, 3).map((ip: string, i: number) => (
                <code key={`ip-${i}`} className="text-xs bg-orange-900/20 text-orange-400 px-2 py-0.5 rounded border border-orange-500/20">{ip}</code>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )
}

// Collapsible section helper
function Section({ title, count, countLabel, expanded, onToggle, color, children }: {
  title: string
  count: number
  countLabel?: string
  expanded: boolean
  onToggle: () => void
  color: string
  children: React.ReactNode
}) {
  const colorMap: Record<string, { badge: string; text: string; border: string }> = {
    red: { badge: 'bg-red-600', text: 'text-red-400', border: 'border-red-500/20' },
    orange: { badge: 'bg-orange-600', text: 'text-orange-400', border: 'border-orange-500/20' },
    yellow: { badge: 'bg-yellow-600', text: 'text-yellow-400', border: 'border-yellow-500/20' },
    cyan: { badge: 'bg-cyan-600', text: 'text-cyan-400', border: 'border-cyan-500/20' },
    purple: { badge: 'bg-purple-600', text: 'text-purple-400', border: 'border-purple-500/20' },
    emerald: { badge: 'bg-emerald-600', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  }
  const c = colorMap[color] || colorMap.cyan

  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between py-2 border-b ${c.border} hover:opacity-80 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          <span className={`${c.text} text-xs`}>{expanded ? '▾' : '▸'}</span>
          <span className={`${c.text} font-mono text-xs tracking-wider font-semibold`}>{title}</span>
          <Badge className={`${c.badge} text-white text-xs font-mono border-0`}>
            {count} {countLabel || ''}
          </Badge>
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
